import { type FC, type PropsWithChildren, type ReactNode } from 'react';
import { useSuspenseAuthorization } from '../hooks/useSuspenseAuthorization';
import { Access } from '../utils/accessEnum';
import { Unauthorized } from './Unauthorized';

export interface AuthorizationWrapperProps extends PropsWithChildren {
  /**
   * The required access level to render the children. Determines whether the user
   * has the necessary permissions to view the content. If not specified, the
   * validation is skipped, and the children are rendered by default.
   */
  access?: Access | Access[] | null;
  deviceId?: string;
  /**
   * If true, inverts the authorization logic. The component will render its children
   * when the user does NOT have the specified access.
   */
  not?: boolean;
  /** If true and an array of access is provided, return true if one of the access
   * succeds. By default, every access must be valid
   */
  some?: boolean;
  /**
   * Fallback component to show when the user is unauthorized.
   * To hide the item completely, set `fallback` to `null`.
   * By default, an image is displayed representing a "no access" message.
   */
  fallback?: ReactNode;
  onClose?: () => void;
}

const AuthorizationWrapper: FC<AuthorizationWrapperProps> = ({
  access,
  children,
  deviceId,
  onClose,
  not,
  some,
  fallback,
}) => {
  const { validateAccess } = useSuspenseAuthorization(deviceId);

  if (access == null) return children;

  let evaluation = true;

  if (Array.isArray(access)) {
    if (some) evaluation = access.some(validateAccess);
    else evaluation = access.every(validateAccess);
  } else {
    evaluation = validateAccess(access);
  }

  if (not === true) evaluation = !evaluation;

  if (evaluation) {
    return children;
  } else {
    return fallback !== undefined ? fallback : <Unauthorized onClose={onClose} />;
  }
};

export { AuthorizationWrapper };
